<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <form class="" @submit.prevent="">
      <div>
        <base-input
          v-if="
            purchasesInvoiceItemModel.purchasesInvoiceable.type.includes(
              'products'
            )
          "
          :label="`${$t(`COMMON.PRODUCT`)} (*)`"
        >
          <product-selector
            :filterOrganization="purchasesInvoice.organization.id"
            :filterIds="getOrderIds()"
            :productObject="purchasesInvoiceItemModel.purchasesInvoiceable"
            :allowNone="false"
            :showAll="false"
            :filterWarehouse="
              purchasesInvoice.destinationWarehouse
                ? purchasesInvoice.destinationWarehouse.id
                : null
            "
            :filterSupplier="
              purchasesInvoice.issuer ? purchasesInvoice.issuer.id : null
            "
            @productChanged="productChanged"
          />
        </base-input>
        <validation-error :errors="apiValidationErrors.purchasesInvoiceable" />
      </div>

      <base-input
        :label="`${$t('COMMON.CODE')} (*)`"
        :placeholder="$t('COMMON.CODE')"
        v-model="purchasesInvoiceItemModel.code"
        input-classes="form-control-alternative"
        v-if="!purchasesInvoiceItemModel.purchasesInvoiceable.type"
      >
      </base-input>
      <validation-error
        v-if="!purchasesInvoiceItemModel.purchasesInvoiceable.type"
        :errors="apiValidationErrors.code"
      />

      <base-input
        :label="$t('COMMON.EXCERPT')"
        :placeholder="$t('COMMON.EXCERPT')"
        input-classes="form-control-alternative"
      >
        <html-editor
          v-model="purchasesInvoiceItemModel.excerpt"
          @change="onFormChanged()"
        >
        </html-editor>
      </base-input>
      <validation-error :errors="apiValidationErrors.excerpt" />

      <div class="row px-3">
        <div class="col-4">
          <base-input
            :label="`${$t('COMMON.QUANTITY')} (*)`"
            :placeholder="$t('COMMON.QUANTITY')"
            v-model="purchasesInvoiceItemModel.quantity"
            type="number"
            step="1"
            input-classes="form-control-alternative"
          >
          </base-input>
          <validation-error :errors="apiValidationErrors.quantity" />
        </div>
        <div class="col-4">
          <base-input
            :label="$t('COMMON.UNIT_OF_MEASURE_UNIT')"
            :placeholder="$t('COMMON.UNIT_OF_MEASURE_UNIT')"
            input-classes="form-control-alternative"
          >
            <unit-of-measure-unit-selector
              :filterOrganization="purchasesInvoice.organization.id"
              :filterUnitOfMeasure="unitOfMeasure?.id"
              :unit="purchasesInvoiceItemModel.unitOfMeasureUnit?.id"
              :allowNone="false"
              :showAll="false"
              @unitChanged="
                (unit) => {
                  unitOfMeasureUnitChanged(unit);
                }
              "
            />
          </base-input>
          <validation-error :errors="apiValidationErrors.taxGroups" />
        </div>
        <div class="col-4">
          <base-input
            :label="`${$t('COMMON.UNIT_PRICE')} (*)`"
            :placeholder="$t('COMMON.UNIT_PRICE')"
            v-model="purchasesInvoiceItemModel.unit_price"
            type="number"
            step="0.0001"
            input-classes="form-control-alternative"
          >
          </base-input>
          <validation-error :errors="apiValidationErrors.unit_price" />
        </div>
      </div>

      <template v-if="!purchasesInvoice.has_no_taxes">
        <base-input
          :label="$t('COMMON.TAX_GROUPS')"
          :placeholder="$t('COMMON.TAX_GROUPS')"
          input-classes="form-control-alternative"
        >
          <tax-groups-selector
            :taxGroups="purchasesInvoiceItemModel.taxGroups"
            @taxGroupsChanged="
              (taxGroups) => {
                purchasesInvoiceItemModel.taxGroups = taxGroups;
              }
            "
          />
          <validation-error :errors="apiValidationErrors.taxGroups" />
        </base-input>
      </template>

      <base-input
        :label="$t('COMMON.DISCOUNT_PERCENT')"
        :placeholder="$t('COMMON.DISCOUNT_PERCENT')"
        v-model="purchasesInvoiceItemModel.discount"
        type="number"
        step="0.0001"
        input-classes="form-control-alternative"
      >
      </base-input>
      <validation-error :errors="apiValidationErrors.discount" />
    </form>

    <template slot="footer">
      <button
        v-if="!purchasesInvoiceItemModel.id"
        type="submit"
        class="btn btn-primary"
        @click="submitPurchasesInvoiceItemForm"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("PURCHASES_INVOICES.ADD_PURCHASES_INVOICE_ITEM") }}
      </button>

      <base-button
        v-if="purchasesInvoiceItemModel.id"
        native-type="submit"
        type="primary"
        class="new-purchases-invoice-item--add"
        @click="submitPurchasesInvoiceItemForm"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("PURCHASES_INVOICES.UPDATE_PURCHASES_INVOICE_ITEM") }}
      </base-button>

      <base-button
        v-if="purchasesInvoiceItemModel.id"
        type="danger"
        @click="deletePurchasesInvoiceItem(purchasesInvoiceItemModel.id)"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("PURCHASES_INVOICES.DELETE_PURCHASES_INVOICE_ITEM") }}
      </base-button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closePurchasesInvoiceItemModal"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import {
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import { cloneDeep } from "lodash";
import formMixin from "@/mixins/form-mixin";
import requestErrorMixin from "@/mixins/request-error-mixin";
import ValidationError from "@/components/ValidationError.vue";
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import ProductSelector from "@/components/ProductSelector.vue";
import defaultPurchasesInvoiceItem from "../defaultPurchasesInvoiceItem";
import UnitOfMeasureUnitSelector from "@/components/UnitOfMeasureUnitSelector.vue";
import TaxGroupsSelector from "@/components/TaxGroupsSelector.vue";

export default {
  name: "purchases-invoice-view-details-purchases-invoice-item-form",

  components: {
    HtmlEditor,
    ValidationError,
    ProductSelector,
    TaxGroupsSelector,
    UnitOfMeasureUnitSelector,
    [Tooltip.name]: Tooltip,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [formMixin, requestErrorMixin],

  props: [
    "purchasesInvoice",
    "purchasesInvoiceItem",
    "showModal",
    "loading",
    "formErrors",
  ],

  data() {
    return {
      all_purchasesInvoiceables: [],
      purchasesInvoiceItemModel: cloneDeep(defaultPurchasesInvoiceItem),
      unitOfMeasure: null,
    };
  },

  computed: {},

  methods: {
    getOrderIds() {
      if (!this.purchasesInvoice.purchasesOrder) {
        return null;
      }
      if (this.purchasesInvoice.purchasesOrder.invoicingItemsState) {
        const remainingProductsIds = Object.values(
          this.purchasesInvoice.purchasesOrder.invoicingItemsState
            .remainingItems
        ).map((item) => item.product_id);
        if (remainingProductsIds.length > 0) {
          return remainingProductsIds;
        }
      }
      return null;
    },

    closePurchasesInvoiceItemModal() {
      this.$emit("onClosePurchasesInvoiceItemModal", true);
    },

    submitPurchasesInvoiceItemForm() {
      const purchasesInvoiceItemData = cloneDeep(
        this.purchasesInvoiceItemModel
      );
      purchasesInvoiceItemData.unit_price = parseFloat(
        purchasesInvoiceItemData.unit_price
      );
      purchasesInvoiceItemData.quantity = parseInt(
        purchasesInvoiceItemData.quantity,
        10
      );
      purchasesInvoiceItemData.discount = parseFloat(
        purchasesInvoiceItemData.discount
      );
      if (!purchasesInvoiceItemData.purchasesInvoiceable.id) {
        purchasesInvoiceItemData.purchasesInvoiceable = null;
      }

      this.$emit("onSubmitPurchasesInvoiceItemForm", purchasesInvoiceItemData);
    },

    deletePurchasesInvoiceItem(purchasesInvoiceItemId) {
      this.$emit("onDeletePurchasesInvoiceItem", purchasesInvoiceItemId);
    },

    async unitOfMeasureUnitChanged(unit) {
      const productPricing = await this.$getProductUnitOfMeasureUnitPrice(
        this.purchasesInvoiceItemModel.purchasesInvoiceable,
        unit.id
      );
      this.purchasesInvoiceItemModel.unitOfMeasureUnit = unit;
      this.purchasesInvoiceItemModel.unit_price = productPricing.buying_price;
    },

    productChanged(productId, product) {
      this.purchasesInvoiceItemModel.purchasesInvoiceable = product;
      this.purchasesInvoiceItemModel.excerpt = product.denomination;

      if (product.warehouseProduct) {
        this.purchasesInvoiceItemModel.unit_price =
          product.warehouseProduct.buying_price;
        this.purchasesInvoiceItemModel.code =
          product.warehouseProduct.sku || product.sku || product.code;
      } else if (product.supplierProduct) {
        this.purchasesInvoiceItemModel.unit_price =
          product.supplierProduct.buying_price;
        this.purchasesInvoiceItemModel.code =
          product.supplierProduct.sku || product.sku || product.code;
      } else {
        this.purchasesInvoiceItemModel.unit_price = product.buying_price;
        this.purchasesInvoiceItemModel.code = product.code;
      }

      if (product.unitOfMeasure) {
        this.purchasesInvoiceItemModel.unitOfMeasureUnit =
          product.unitOfMeasure.referenceUnit;
      }
      if (!this.purchasesInvoice.has_no_taxes) {
        this.purchasesInvoiceItemModel.taxGroups = product.taxGroups;
      } else {
        this.purchasesInvoiceItemModel.taxGroups = [];
      }
      this.unitOfMeasure = product.unitOfMeasure;
    },
  },

  mounted() {},

  watch: {
    purchasesInvoiceItem(purchasesInvoiceItem) {
      if (purchasesInvoiceItem) {
        this.purchasesInvoiceItemModel = cloneDeep(purchasesInvoiceItem);
        this.unitOfMeasure =
          purchasesInvoiceItem.unitOfMeasureUnit.unitOfMeasure;
      }
      this.resetApiValidation();
    },

    formErrors(errors) {
      if (errors) {
        this.setApiValidation(errors);
      }
    },
  },
};
</script>
